import React, { useContext, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import useTranslator from "../../hooks/useTranslator";
import { useNavigate, useParams } from "react-router-dom";
import AuthContext from "../../context/auth/AuthContext";
import {
  formatPropertyAddress,
  haveValue,
  shouldDeductNextView,
} from "../../helper/common";
import LandLordContext from "../../context/landlord/LandLordContext";
import Cancelimg from "../../assets/img/cancel-01.svg";
import CreditDeductionPopup from "../../pages/landlord/profile-request/CreditDeductionPopup";
import ReportOverviewDrawer from "../ReportOverviewDrawer";

const AwardPropertyPopup = ({
  awardPopupModal,
  setAwardPopupModal,
  selectedTenants,
  property,
  rentVal,
  currentLang,
  selectionModel,
  historyEventId,
  onResolutionChange,
}) => {
  const navigate = useNavigate();
  const { _t } = useTranslator();
  const { workspaceId } = useParams();
  const [rent, setRent] = useState(property?.report_rent ?? null);
  const [deposit, setDeposit] = useState(property?.report_deposit ?? null);
  const [openConfirmationPopup, setOpenConfirmationPopup] = useState(false);
  const { updateRentDepositReport, deaWorkspace } = useContext(LandLordContext);
  const { user, setLoading } = useContext(AuthContext);
  const [rootNavigate, setRootNavigate] = useState(null);
  const [totalProfilesForDeduction, setTotalProfilesForDeduction] =
    useState(null);
  const [showReportOverviewDrawer, setShowReportOverviewDrawer] =
    useState(false);
  const [reportOverviewQueryParams, setReportOverviewQueryParams] =
    useState(null);
  const [reportKey, setReportKey] = useState(0);
  const lastReportSelectionKey = useRef(null);

  useEffect(() => {
    setRent(property?.report_rent);
    setDeposit(property?.report_deposit);
  }, [property]);

  useEffect(() => {
    if (awardPopupModal) {
      handleGenerateReport();
      setAwardPopupModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awardPopupModal]);

  const handleChangeRent = (e) => {
    setRent(e.target.value);
  };

  const handleDepositChange = (e) => {
    setDeposit(e.target.value);
  };

  const calculateOpenPrice = async (skipLoading = false) => {
    try {
      if (!skipLoading) {
        setLoading(true);
      }
      let numberOfTenantsToDeductPrice = 0;

      for (let tProfile of selectedTenants) {
        const checkView = await shouldDeductNextView(user, tProfile?._id);
        if (checkView) {
          numberOfTenantsToDeductPrice++;
        }
      }

      return numberOfTenantsToDeductPrice;
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const buildReportQueryParams = (tenants) => {
    let queryParams = "";

    const params = new URLSearchParams(window.location.search);
    params.set("propertyId", property?._id);

    tenants.forEach((tenantProfile) => {
      queryParams += `sharedprofileid=${tenantProfile?._id}&`;
      params.append("sharedprofileid", tenantProfile?._id);
    });
    queryParams += `rent=${rentVal}`;
    if (haveValue(property?._id)) {
      queryParams += `&property=${property?._id}`;
    }

    const propertyName = formatPropertyAddress(property, currentLang);

    params.set("propName", propertyName);

    const redirectUrl = `${window.location.pathname}?${params.toString()}`;
    queryParams += `&redirectUrl=${encodeURIComponent(redirectUrl)}`;

    if (selectionModel && selectionModel.length > 0) {
      queryParams += `&selection=${encodeURIComponent(
        JSON.stringify(selectionModel)
      )}`;
    }

    if (property?.is_awarded) {
      queryParams += `&awardSnapshotCompleted=true`;
    }

    if (haveValue(historyEventId)) {
      queryParams += `&historyEventId=${encodeURIComponent(historyEventId)}`;
    }

    return queryParams;
  };

  const selectionKey = (tenants) =>
    (tenants || []).map((t) => t?._id).join(",");

  useEffect(() => {
    if (!showReportOverviewDrawer) return;
    const tenants = selectedTenants || [];
    const key = selectionKey(tenants);
    if (key === lastReportSelectionKey.current) return;
    lastReportSelectionKey.current = key;
    if (tenants.length === 0) {
      setShowReportOverviewDrawer(false);
      return;
    }
    setReportOverviewQueryParams(buildReportQueryParams(tenants));
    setReportKey((k) => k + 1);
  }, [selectedTenants, showReportOverviewDrawer]);

  const handleGenerateReport = () => {
    const data_temp = {
      report_rent: rentVal,
    };
    updateRentDepositReport(property?._id, data_temp);

    const totalProfiles = calculateOpenPrice(
      !!user?.current_workspace?.dea_rules_activated
    );

    if (totalProfiles > 0) {
      const queryParams = buildReportQueryParams(selectedTenants);
      setRootNavigate(
        user?.current_workspace?.dea_rules_activated
          ? `${
              haveValue(workspaceId) ? "/" + workspaceId : ""
            }/report-overview?${queryParams}`
          : `${
              haveValue(workspaceId) ? "/" + workspaceId : ""
            }/generatedReport/combinedReport?${queryParams}`
      );
      setOpenConfirmationPopup(true);
      setTotalProfilesForDeduction(totalProfiles);
    } else {
      if (user?.current_workspace?.dea_rules_activated) {
        lastReportSelectionKey.current = selectionKey(selectedTenants);
        setReportOverviewQueryParams(buildReportQueryParams(selectedTenants));
        setReportKey((k) => k + 1);
        setShowReportOverviewDrawer(true);
      } else {
        navigate(
          `${
            haveValue(workspaceId) ? "/" + workspaceId : ""
          }/generatedReport/combinedReport?${buildReportQueryParams(
            selectedTenants
          )}`
        );
      }
    }
  };

  return (
    <>
      <Modal
        show={false}
        className="metermodal wrapper-modal modal-xl custom-modal-popup"
        centered
        onHide={() => setAwardPopupModal(false)}
      >
        <Modal.Header>
          <h2 className="modal-title-text">
            {_t(
              "generateReportAndAwardPropertyHeading",
              "Award property/Generate report popup heading when LL selects tenants from property list.."
            )}
          </h2>
          <div
            className="cursor-pointer"
            onClick={() => setAwardPopupModal(false)}
          >
            <img
              src={Cancelimg}
              alt="icon"
              className="canceicon topRightIcon"
            />
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="bodyContentset">
            <form onSubmit={(e) => e.preventDefault()}>
              <div className="forminput customInput">
                <label className="forminput label-text">
                  <h6 className="modal-label">
                    {_t("Rent Price", "Award property popup form label")}
                  </h6>
                </label>
                <input
                  type="number"
                  name="Rent Price"
                  className="modal-input"
                  placeholder={_t(
                    "Enter Rent Price",
                    "Award property popup form placeholder"
                  )}
                  value={rent}
                  onChange={handleChangeRent}
                />
              </div>
              <div className="forminput customInput">
                <label className="forminput label-text">
                  <h6 className="modal-label">
                    {_t(
                      "Number Of Deposit Month",
                      "Award property popup form label"
                    )}
                  </h6>
                </label>
                <input
                  type="number"
                  name="Enter months"
                  className="modal-input"
                  onChange={handleDepositChange}
                  placeholder={_t(
                    "Enter months",
                    "Award property popup form placeholder"
                  )}
                  value={deposit}
                />
              </div>
              <div className=" row100 gridbar popup_btn2">
                <button
                  className="addGuarantorButton modal-btn"
                  type="submit"
                  onClick={handleGenerateReport}
                  disabled={!haveValue(rent) || !haveValue(deposit)}
                >
                  {_t("submit")}
                </button>
              </div>
            </form>
          </div>
        </Modal.Body>
      </Modal>
      <CreditDeductionPopup
        openModal={openConfirmationPopup}
        setOpenModal={setOpenConfirmationPopup}
        openReport={() => navigate(rootNavigate)}
        totalProfiles={totalProfilesForDeduction}
      />
      <ReportOverviewDrawer
        isOpen={showReportOverviewDrawer}
        onClose={() => setShowReportOverviewDrawer(false)}
        queryParameters={reportOverviewQueryParams}
        reportKey={reportKey}
        onResolutionChange={onResolutionChange}
      />
    </>
  );
};

export default AwardPropertyPopup;
